import type { AudioSource } from 'expo-audio'
import { useCallback, useEffect, useState } from 'react'
import {
  ActivityIndicator,
  Platform,
  Pressable,
  Text,
  View,
} from 'react-native'
import {
  setAudioModeAsync,
  useAudioPlayer,
  useAudioPlayerStatus,
} from 'expo-audio'
import { Icon, symbolName } from '@/src/components/ui/Icon'
import { colors } from '@/src/theme'
import { body3, label1, label2 } from '@/src/theme/typography'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

const PLAYBACK_AUDIO_MODE = {
  playsInSilentMode: true,
  allowsRecording: false,
  interruptionMode: 'mixWithOthers' as const,
}

const WAVE_HEIGHT_PX = 35
const WAVE_BAR_MAX_PX = 32
/** Bar thickness — Figma recording waveform (~3px) */
const WAVE_BAR_WIDTH_PX = 3

const WAVE_BAR_COUNT = 52

type HeaderBackedAudioSource = {
  uri: string
  headers: Record<string, string>
}

/**
 * Amplitudes 0–1 for each bar: smooth triple-lobe envelope (design silhouette)
 * plus small sine ripples so height changes gradually bar-to-bar.
 */
function buildDecorativeWaveAmplitudes(barCount: number): number[] {
  const gaussian = (t: number, center: number, sigma: number): number => {
    const z = (t - center) / sigma
    return Math.exp(-(z * z))
  }

  const out: number[] = []
  for (let i = 0; i < barCount; i += 1) {
    const t = barCount <= 1 ? 0.5 : i / (barCount - 1)
    const envelope =
      0.06 +
      0.78 *
        (gaussian(t, 0.21, 0.13) +
          gaussian(t, 0.51, 0.16) * 1.1 +
          gaussian(t, 0.79, 0.12))
    const ripple =
      0.05 * Math.sin(t * Math.PI * 2 * 11.5 + 0.4) +
      0.028 * Math.sin(t * Math.PI * 2 * 20 + 1.1)
    const v = envelope + ripple
    out.push(Math.max(0.06, Math.min(0.98, v)))
  }
  return out
}

const WAVE_BARS = buildDecorativeWaveAmplitudes(WAVE_BAR_COUNT)

function isHeaderBackedAudioSource(
  source: AudioSource | undefined,
): source is HeaderBackedAudioSource {
  return (
    source != null &&
    typeof source === 'object' &&
    typeof source.uri === 'string' &&
    source.headers != null &&
    Object.keys(source.headers).length > 0
  )
}

function getRecordingSourceKey(recordingSource: AudioSource | undefined) {
  if (recordingSource == null) {
    return 'empty'
  }

  if (typeof recordingSource === 'string') {
    return recordingSource
  }

  if (isHeaderBackedAudioSource(recordingSource)) {
    return `${recordingSource.uri}:${JSON.stringify(recordingSource.headers)}`
  }

  if (
    typeof recordingSource === 'object' &&
    'uri' in recordingSource &&
    typeof recordingSource.uri === 'string'
  ) {
    return recordingSource.uri
  }

  return JSON.stringify(recordingSource)
}

async function fetchAuthenticatedRecordingBlobSource({
  headers,
  uri,
}: HeaderBackedAudioSource): Promise<AudioSource> {
  const response = await fetch(uri, { headers })
  if (!response.ok) {
    throw new Error(
      `Failed to load measurement recording: ${response.status} ${response.statusText}`,
    )
  }

  const blob = await response.blob()
  return { uri: URL.createObjectURL(blob) }
}

function usePlayableRecordingSource(recordingSource: AudioSource | undefined) {
  const needsAuthenticatedWebFetch =
    Platform.OS === 'web' && isHeaderBackedAudioSource(recordingSource)
  const authenticatedSource = needsAuthenticatedWebFetch
    ? recordingSource
    : null
  const query = useQuery({
    enabled: authenticatedSource != null,
    gcTime: 0,
    queryKey: [
      'voice-assessment-recording-blob-source',
      authenticatedSource?.uri ?? null,
      authenticatedSource?.headers ?? null,
    ],
    queryFn: () => fetchAuthenticatedRecordingBlobSource(authenticatedSource!),
    retry: false,
  })

  useEffect(() => {
    const blobUrl =
      typeof query.data === 'object' &&
      query.data != null &&
      'uri' in query.data &&
      typeof query.data.uri === 'string'
        ? query.data.uri
        : null

    return () => {
      if (blobUrl != null) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [query.data])

  useEffect(() => {
    if (query.error != null) {
      console.error('Voice assessment recording fetch failed', query.error)
    }
  }, [query.error])

  return {
    source: needsAuthenticatedWebFetch
      ? (query.data ?? null)
      : (recordingSource ?? null),
    isLoading:
      needsAuthenticatedWebFetch &&
      (query.isPending || (query.isFetching && query.data == null)),
    loadError: query.error,
    retry: query.refetch,
  }
}

interface VoiceAssessmentRecordingPlaceholderProps {
  durationLabel: string
  flat?: boolean
  recordingSource?: AudioSource
}

interface VoiceAssessmentRecordingPlaceholderInnerProps {
  durationLabel: string
  flat: boolean
  recordingSource?: AudioSource
}

export function VoiceAssessmentRecordingPlaceholder({
  durationLabel,
  flat = false,
  recordingSource,
}: VoiceAssessmentRecordingPlaceholderProps) {
  return (
    <VoiceAssessmentRecordingPlaceholderInner
      key={getRecordingSourceKey(recordingSource)}
      durationLabel={durationLabel}
      flat={flat}
      recordingSource={recordingSource}
    />
  )
}

function VoiceAssessmentRecordingPlaceholderInner({
  durationLabel,
  flat,
  recordingSource,
}: VoiceAssessmentRecordingPlaceholderInnerProps) {
  const { t } = useTranslation(['statistics'])
  const {
    source: playableRecordingSource,
    isLoading,
    loadError,
    retry,
  } = usePlayableRecordingSource(recordingSource)
  const [playbackError, setPlaybackError] = useState<Error | null>(null)
  const player = useAudioPlayer(playableRecordingSource)
  const playerStatus = useAudioPlayerStatus(player)
  const hasRecordingSource = playableRecordingSource != null
  const isPlaying = playerStatus.playing
  const hasPlayerLoadError =
    playableRecordingSource != null &&
    playerStatus.isLoaded === false &&
    typeof playerStatus.playbackState === 'string' &&
    playerStatus.playbackState.toLowerCase().includes('error')
  const hasLoadError = loadError != null
  const hasPlaybackError = playbackError != null
  const hasRecordingError =
    hasLoadError || hasPlaybackError || hasPlayerLoadError

  const handleRetry = useCallback(() => {
    setPlaybackError(null)
    void retry()
  }, [retry])

  const handleTogglePlayback = useCallback(() => {
    const togglePlayback = async () => {
      if (!hasRecordingSource || hasRecordingError) {
        return
      }

      try {
        await setAudioModeAsync(PLAYBACK_AUDIO_MODE)

        if (isPlaying) {
          await Promise.resolve(player.pause())
          await Promise.resolve(player.seekTo(0))
          return
        }

        await Promise.resolve(player.seekTo(0))
        await Promise.resolve(player.play())
      } catch (error) {
        setPlaybackError(
          error instanceof Error ? error : new Error(String(error)),
        )
        console.error('Voice assessment demo recording playback failed', error)
      }
    }

    void togglePlayback()
  }, [hasRecordingError, hasRecordingSource, isPlaying, player])

  return (
    <View
      className={`min-h-[83px] gap-2 rounded-lg bg-background-secondary px-4 py-3 ${flat ? '' : 'border border-border'}`}
    >
      <View className="flex-row items-center justify-between">
        <Text style={[body3, { color: colors.textBody }]}>
          {t('statistics:voice_assessment_recording_label')}
        </Text>
        <Text style={[body3, { color: colors.textBody }]}>{durationLabel}</Text>
      </View>
      {isLoading ? (
        <View
          accessibilityRole="progressbar"
          className="min-h-[35px] flex-row items-center gap-2"
        >
          <ActivityIndicator color={colors.primary} size="small" />
          <Text style={[body3, { color: colors.inkSecondary }]}>
            {t('statistics:voice_assessment_recording_loading')}
          </Text>
        </View>
      ) : hasLoadError || hasPlaybackError || hasPlayerLoadError ? (
        <View
          accessibilityRole="alert"
          accessibilityHint={playbackError?.message}
          className="gap-1.5 rounded-md border px-3 py-3"
          style={{
            borderColor: colors.destructive,
            backgroundColor: colors.errorBackground,
          }}
        >
          <View className="flex-row items-center gap-2">
            <Icon
              name={symbolName('exclamationmark.triangle.fill', 'warning')}
              size={16}
              color={colors.destructive}
            />
            <Text style={[label1, { color: colors.ink }]}>
              {t('statistics:voice_assessment_recording_error_title')}
            </Text>
          </View>
          <Text style={[body3, { color: colors.inkSecondary }]}>
            {t('statistics:voice_assessment_recording_error_message')}
          </Text>
          <Pressable
            onPress={handleRetry}
            accessibilityRole="button"
            accessibilityLabel={t(
              'statistics:voice_assessment_recording_retry_button',
            )}
            className="mt-1 h-9 self-start items-center justify-center rounded-lg bg-primary px-4 active:opacity-80"
          >
            <Text style={[label2, { color: colors.primaryForeground }]}>
              {t('statistics:voice_assessment_recording_retry_button')}
            </Text>
          </Pressable>
        </View>
      ) : (
        <Pressable
          onPress={handleTogglePlayback}
          disabled={!hasRecordingSource}
          accessibilityRole="button"
          accessibilityLabel={t('statistics:voice_assessment_recording_label')}
          accessibilityState={{
            disabled: !hasRecordingSource,
            busy: isLoading,
            selected: isPlaying,
          }}
          className={`flex-row items-center justify-between gap-2 active:opacity-70 ${hasRecordingSource ? '' : 'opacity-60'}`}
        >
          <View
            className="min-h-[35px] flex-1 flex-row items-center gap-[2px]"
            style={{ height: WAVE_HEIGHT_PX }}
          >
            {WAVE_BARS.map((ratio, index) => (
              <View
                key={`wave-${String(index)}`}
                className="h-full flex-1 items-center justify-center"
              >
                <View
                  className="rounded-[1px]"
                  style={{
                    width: WAVE_BAR_WIDTH_PX,
                    height: Math.max(6, Math.round(ratio * WAVE_BAR_MAX_PX)),
                    backgroundColor: colors.primary,
                  }}
                />
              </View>
            ))}
          </View>
          <View className="size-6 shrink-0 items-center justify-center">
            <Icon
              name={
                isPlaying
                  ? symbolName('stop.circle', 'stop_circle')
                  : symbolName('play.circle', 'play_circle')
              }
              size={24}
              color="ink"
            />
          </View>
        </Pressable>
      )}
    </View>
  )
}
